import { ReactElement, useRef, useState } from 'react';

import { useModal } from 'mui-modal-provider';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Dialog,
  HexIcon,
  ListItem,
  ListItemText,
  Select,
  HexIconElementProps,
  ShowModalOutput,
  Placeholder as StatusMessage,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

import {
  OptimizationType,
  PreparedWorkload,
} from 'features/workload-optimization';

import { OptimizationTypeSelectFooter } from '../_components/OptimizationTypeSelectFooter';
import {
  OPTIMIZATION_TYPE_NON_HORIZONTAL_OPTIONS,
  OPTIMIZATION_TYPE_OPTIONS,
} from '../_constants';
import { useHpaV2Status, HpaV2Status } from './useHpaV2Status';
import { workloadHasHpaV2Settings } from '../_utils';

import {
  useCluster,
  ClusterProvider,
} from '@cast/shared-cluster-context-data-access';

type Props = {
  action: 'override' | 'revertToPolicy';
  initialValue: OptimizationType | null;
  title: string;
  titleName?: string;
  icon: ReactElement<HexIconElementProps>;
  confirmBtnLabel: string;
  testId?: string;
  workloads: PreparedWorkload[];
  onCancel?: () => void;
  onApply: ({ appliedType }: { appliedType: OptimizationType }) => void;
};

type OptimizationTypeSelectorDialogProps = Props & {
  close: () => void;
  apply: ({ appliedType }: { appliedType: OptimizationType }) => void;
  cluster: Cluster;
  hpaV2Status: HpaV2Status;
};

const OptimizationTypeSelectorDialogContent = ({
  workloads,
  action,
  initialValue,
  title,
  titleName,
  icon,
  close,
  apply,
  value,
  setValue,
  hpaV2Status,
}: {
  workloads: PreparedWorkload[];
  action: 'override' | 'revertToPolicy';
  initialValue: OptimizationType | null;
  title: string;
  titleName?: string;
  icon: ReactElement<HexIconElementProps>;
  close: () => void;
  apply: ({ appliedType }: { appliedType: OptimizationType }) => void;
  value: OptimizationType | null;
  setValue: (value: OptimizationType | null) => void;
  hpaV2Status: HpaV2Status;
}) => {
  const isHpaSettingsMissing =
    workloads.some((workload) => !workloadHasHpaV2Settings(workload)) ||
    hpaV2Status === 'outdated';

  return (
    <StatusMessage
      sx={{ position: 'relative', top: -42 }}
      testId="optimization-type-selector-dialog-content"
      icon={
        action === 'override' ? (
          <HexIcon variant="check" icon={icon} size={84} />
        ) : (
          <HexIcon variant="notification" icon={icon} size={84} />
        )
      }
      titleProps={{ component: 'div', variant: 'h5', maxWidth: 480 }}
      title={
        <>
          {titleName && (
            <Typography
              variant="A1"
              color={tokens.theme.fg.structural.secondary}
              pt="8px"
            >
              {titleName}
            </Typography>
          )}
          <Typography variant="h5">{title}</Typography>
        </>
      }
      body={
        <Stack maxWidth={400} gap={16} mt={16}>
          {action === 'override' && (
            <Select
              testId="optimization-type-selector"
              applySortingWhileSearching={false}
              value={value}
              onChange={(newValue) => {
                setValue(newValue);
              }}
              inputProps={{
                placeholder: 'Select optimization type',
              }}
              renderOption={(option, listItemProps) => {
                return (
                  <ListItem {...listItemProps}>
                    <ListItemText
                      primary={option}
                      sx={{ py: 10 }}
                      primaryTypographyProps={{ typography: 'P12R' }}
                    />
                  </ListItem>
                );
              }}
              options={
                isHpaSettingsMissing
                  ? OPTIMIZATION_TYPE_NON_HORIZONTAL_OPTIONS
                  : OPTIMIZATION_TYPE_OPTIONS
              }
              Footer={
                isHpaSettingsMissing && (
                  <OptimizationTypeSelectFooter
                    message={
                      hpaV2Status === 'outdated'
                        ? 'To enable horizontal scaling, update Workload Autoscaler version'
                        : 'To enable horizontal scaling, configure it in the policy.'
                    }
                    disabledItemsTypography="P12R"
                    messageTypography="P12M"
                  />
                )
              }
            />
          )}

          <Typography variant="P14R">
            {action === 'override'
              ? `${CAST_AI} can automatically adjust
              workload resource requests based on your policy settings. The
              workload may restart during optimization.`
              : 'Optimization type will be reverted to the policy value.'}
          </Typography>
        </Stack>
      }
      actions={
        <Stack direction="row" gap={16}>
          <Button variant="secondary" onClick={close} testId="cancel-button">
            Cancel
          </Button>

          <Button
            disabled={
              action === 'override' &&
              (value === initialValue || value === null)
            }
            variant="primary"
            onClick={() => {
              if (value === null) {
                return;
              }

              apply({ appliedType: value });
            }}
            testId="apply-button"
          >
            {action === 'override' ? 'Apply override' : 'Revert to policy'}
          </Button>
        </Stack>
      }
    />
  );
};

const OptimizationTypeSelectorDialog = ({
  action,
  workloads,
  initialValue,
  title,
  titleName,
  icon,
  close,
  apply,
  cluster,
  hpaV2Status,
}: OptimizationTypeSelectorDialogProps) => {
  const [value, setValue] = useState<OptimizationType | null>(initialValue);

  return (
    <Dialog
      open
      PaperProps={{ sx: { overflow: 'visible' } }}
      testId="optimization-type-selector-dialog"
    >
      <ClusterProvider cluster={cluster}>
        <OptimizationTypeSelectorDialogContent
          workloads={workloads}
          action={action}
          initialValue={initialValue}
          title={title}
          titleName={titleName}
          icon={icon}
          close={close}
          apply={apply}
          value={value}
          setValue={setValue}
          hpaV2Status={hpaV2Status}
        />
      </ClusterProvider>
    </Dialog>
  );
};

type UseToggleWorkloadOptimizationChangeDialogParams = {
  hpaV2Status?: HpaV2Status;
};

// This hook and dialog are introduce, cuz if we will try to reuse useToggleWorkloadOptimizationConfirmationDialog it will become unreadable, too complex and hard to maintain.
export const useToggleWorkloadOptimizationChangeDialog = ({
  hpaV2Status: hpaV2StatusProp,
}: UseToggleWorkloadOptimizationChangeDialogParams = {}) => {
  const { showModal } = useModal();
  const modalInstanceRef = useRef<ShowModalOutput | null>(null);
  const { cluster } = useCluster();
  const { hpaV2Status: hpaV2StatusFromHook } = useHpaV2Status();
  const hpaV2Status = hpaV2StatusProp ?? hpaV2StatusFromHook;

  return {
    openOptimizationChangeDialog: (props: Props) => {
      modalInstanceRef.current = showModal(OptimizationTypeSelectorDialog, {
        ...props,
        cluster,
        hpaV2Status,
        apply: ({ appliedType }) => {
          modalInstanceRef.current?.destroy();
          props.onApply?.({ appliedType });
        },
        close: () => {
          modalInstanceRef.current?.destroy();
          props.onCancel?.();
        },
      });
    },
    closeOptimizationChangeDialog: () => {
      modalInstanceRef.current?.destroy();
    },
  };
};
